/**
 * IndexedDB database name used for persisted signal-store data.
 */
export const PERSIST_STATE_DB_NAME = 'awork'

/**
 * Current IndexedDB schema version for persisted signal-store data.
 * Increase this only when the persisted schema changes.
 */
export const PERSIST_STATE_DB_VERSION = 9

/**
 * Legacy IndexedDB object store used in version 4 and earlier.
 */
export const PERSIST_STATE_LEGACY_STORE_NAME = 'awState'

/**
 * Reserved row key used for persisted metadata in dedicated object stores.
 */
export const PERSIST_STATE_META_ROW_KEY = '__meta__'

/**
 * Initial metadata row written to every configured object store.
 */
export const PERSIST_STATE_EMPTY_META_ROW: Record<string, never> = {}

/**
 * Declares how one persisted logical store is represented inside IndexedDB.
 */
export interface PersistedStoreDefinition {
  name: string
  type: 'plain' | 'entity'
}

/**
 * Production signal-store schema managed by developers.
 */
export const APP_PERSISTED_STORES = [
  { name: 'account', type: 'plain' },
  { name: 'agent', type: 'entity' },
  { name: 'agentConnector', type: 'entity' },
  { name: 'agentPrompt', type: 'entity' },
  { name: 'agentThread', type: 'entity' },
  { name: 'agentSkill', type: 'entity' },
  { name: 'app', type: 'plain' },
  { name: 'checklistItem', type: 'entity' },
  { name: 'comment', type: 'entity' },
  { name: 'company', type: 'entity' },
  { name: 'customFieldDefinition', type: 'entity' },
  { name: 'dashboard', type: 'entity' },
  { name: 'demoData', type: 'plain' },
  { name: 'document', type: 'entity' },
  { name: 'documentSpace', type: 'entity' },
  { name: 'draftFilter', type: 'entity' },
  { name: 'entityFilter', type: 'entity' },
  { name: 'event', type: 'plain' },
  { name: 'featureFlag', type: 'plain' },
  { name: 'integration', type: 'entity' },
  { name: 'logs', type: 'plain' },
  { name: 'notification', type: 'entity' },
  { name: 'onboardingTaskProgress', type: 'entity' },
  { name: 'permissions', type: 'plain' },
  { name: 'planningSession', type: 'entity' },
  { name: 'project', type: 'entity' },
  { name: 'projectTemplate', type: 'entity' },
  { name: 'projectType', type: 'entity' },
  { name: 'projectStatus', type: 'entity' },
  { name: 'reportDashboard', type: 'entity' },
  { name: 'settings', type: 'plain' },
  { name: 'subscription', type: 'plain' },
  { name: 'subscriptionPlan', type: 'entity' },
  { name: 'task', type: 'entity' },
  { name: 'taskList', type: 'entity' },
  { name: 'taskSchedule', type: 'entity' },
  { name: 'taskSettings', type: 'plain' },
  { name: 'taskStatus', type: 'entity' },
  { name: 'taskView', type: 'entity' },
  { name: 'team', type: 'entity' },
  { name: 'timeBooking', type: 'entity' },
  { name: 'timeTracking', type: 'entity' },
  { name: 'timeTrackingReport', type: 'entity' },
  { name: 'timeTrackingSettings', type: 'plain' },
  { name: 'user', type: 'entity' },
  { name: 'workflow', type: 'entity' },
  { name: 'workspace', type: 'entity' },
  { name: 'workType', type: 'entity' }
] as const satisfies readonly PersistedStoreDefinition[]

const APP_PERSISTED_STORE_DEFINITION_MAP = new Map<string, PersistedStoreDefinition>(
  APP_PERSISTED_STORES.map(store => [store.name, store])
)

/**
 * Builds the complete list of configured persisted store names.
 * @param {Iterable<PersistedStoreDefinition>} [additionalStoreDefinitions=[]] Optional test-only store definitions.
 * @returns {string[]}
 */
export function getConfiguredPersistedStoreNames(
  additionalStoreDefinitions: Iterable<PersistedStoreDefinition> = []
): string[] {
  return Array.from(
    new Set([
      ...APP_PERSISTED_STORES.map(store => store.name),
      ...Array.from(additionalStoreDefinitions, store => store.name)
    ])
  )
}

/**
 * Determines whether a store name is part of the configured IndexedDB schema.
 * @param {string} storeName
 * @param {Iterable<PersistedStoreDefinition>} [additionalStoreDefinitions=[]] Optional test-only store definitions.
 * @returns {boolean}
 */
export function hasConfiguredPersistedStoreName(
  storeName: string,
  additionalStoreDefinitions: Iterable<PersistedStoreDefinition> = []
): boolean {
  return (
    APP_PERSISTED_STORE_DEFINITION_MAP.has(storeName) ||
    Array.from(additionalStoreDefinitions).some(storeDefinition => storeDefinition.name === storeName)
  )
}

/**
 * Returns the configured production store definition when available.
 * @param {string} storeName
 * @returns {PersistedStoreDefinition | undefined}
 */
export function getAppPersistedStoreDefinition(storeName: string): PersistedStoreDefinition | undefined {
  return APP_PERSISTED_STORE_DEFINITION_MAP.get(storeName)
}

/**
 * Returns the configured store definition from production or test-only schema entries.
 * @param {string} storeName
 * @param {Iterable<PersistedStoreDefinition>} [additionalStoreDefinitions=[]] Optional test-only store definitions.
 * @returns {PersistedStoreDefinition | undefined}
 */
export function getConfiguredPersistedStoreDefinition(
  storeName: string,
  additionalStoreDefinitions: Iterable<PersistedStoreDefinition> = []
): PersistedStoreDefinition | undefined {
  return (
    APP_PERSISTED_STORE_DEFINITION_MAP.get(storeName) ||
    Array.from(additionalStoreDefinitions).find(storeDefinition => storeDefinition.name === storeName)
  )
}
